import { Blocks, ExternalLink } from "lucide-react";
import { Link, Outlet } from "react-router";
import { AuthGate } from "@/features/auth/auth-gate";
import { IdentityChip } from "@/features/auth/identity-chip";
import { BLOCKS_REPO, TEMPLATE_REPO } from "@/lib/links";

export function Layout() {
  return (
    // A flex column filling the viewport: header and footer size to content, and
    // <main> takes the rest. `min-h-dvh`, not `min-h-full` — a percentage height
    // resolves against a body that has no height of its own, so the page never
    // filled the screen and short pages bunched at the top with the footer
    // stranded in the middle.
    <div className="flex min-h-dvh flex-col bg-ink-950">
      {/* A single soft accent wash at the top of the page, echoing the lighting
          in the block artwork so the chrome and the images share one world. */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-x-0 top-0 h-80 opacity-50"
        style={{
          background:
            "radial-gradient(60% 100% at 50% 0%, color-mix(in oklab, var(--color-accent-identity) 18%, transparent), transparent 70%)",
        }}
      />

      <header className="relative border-b border-ink-700/60">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-4">
          <Link to="/" className="flex items-center gap-2.5">
            <Blocks size={18} className="text-ink-50" />
            <span className="font-medium tracking-tight text-ink-50">qure blocks</span>
          </Link>
          {/* Blocks are meaningless without the template they install into, so
              the way back to it is in the chrome on every page, not buried in
              a doc. */}
          <nav className="ml-auto flex items-center gap-4 text-xs">
            <a
              href={TEMPLATE_REPO}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 text-ink-400 transition-colors hover:text-ink-50"
            >
              Starter template
              <ExternalLink size={12} />
            </a>
            <a
              href={BLOCKS_REPO}
              target="_blank"
              rel="noreferrer"
              className="hidden items-center gap-1.5 text-ink-400 transition-colors hover:text-ink-50 sm:flex"
            >
              Registry
              <ExternalLink size={12} />
            </a>
            <IdentityChip />
          </nav>
        </div>
      </header>

      {/* The gate wraps only the routed page. The header and footer render
          either way, so a signed-out visitor still lands on something that
          looks like this site rather than a bare login form.

          flex-1 + flex-col so a short page (the sign-in panel, a spinner) can
          claim the leftover height and centre itself in it, while a long one
          (the gallery) just flows normally. */}
      <main className="relative mx-auto flex w-full max-w-6xl flex-1 flex-col px-6 py-8">
        <AuthGate>
          <Outlet />
        </AuthGate>
      </main>

      <footer className="relative mx-auto max-w-6xl space-y-2 px-6 py-10 text-xs text-ink-400">
        <p>
          Each block is a port, its adapters, and a fake. Copy them in — you own the code
          from that point on.
        </p>
        <p>
          Blocks install into the{" "}
          <a
            href={TEMPLATE_REPO}
            target="_blank"
            rel="noreferrer"
            className="text-ink-200 underline underline-offset-4 hover:text-ink-50"
          >
            FastAPI + Vite starter template
          </a>
          . Start there, then add what the app actually needs.
        </p>
      </footer>
    </div>
  );
}
